import React from 'react'

import { useBulkServiceOrders, useQuestions } from '../../_load/ServiceOrderLoader'
import { buildRowReplies, findRowReply, firstInvalidRowReply, gridColumns } from '../_data/rowReplies'
import { cleanRepliesForApi } from '../../../../../utils/helpers/cleanRepliesRequest'

// Contexto opcional: fora do fluxo de execução em massa (modal fechado ou
// tela de execução individual/pública) simplesmente não existe Provider, e
// todos os hooks abaixo degradam para um estado vazio em vez de lançar erro
// — assim `useSaveServiceOrder` pode chamar `useBulkGridPayload` incondicio-
// nalmente sem se importar se está ou não dentro da grade.
const Context = React.createContext(null)

const initRows = (serviceOrders, questions) =>
  (serviceOrders || []).map(serviceOrder => ({
    serviceOrder,
    replies: buildRowReplies(questions, serviceOrder)
  }))

const setRowReplyValue = (row, questionId, value) => ({
  ...row,
  replies: row.replies.map(reply => (reply.questionId === questionId ? { ...reply, value } : reply))
})

const reducer = (state, action) => {
  switch (action.type) {
    case 'TOGGLE_ROW': {
      const selected = new Set(state.selected)
      if (selected.has(action.serviceOrderId)) {
        selected.delete(action.serviceOrderId)
      } else {
        selected.add(action.serviceOrderId)
      }
      return { ...state, selected }
    }

    case 'TOGGLE_ALL': {
      const allSelected = state.rows.length > 0 && state.rows.every(row => state.selected.has(row.serviceOrder.id))
      const selected = allSelected ? new Set() : new Set(state.rows.map(row => row.serviceOrder.id))
      return { ...state, selected }
    }

    // Multi-edição: editar uma célula de uma linha MARCADA replica o mesmo
    // valor para todas as demais linhas marcadas naquela coluna — igual ao
    // `BulkForm` usado no cadastro em massa. Editar uma linha não marcada só
    // afeta ela mesma.
    case 'SET_CELL': {
      const { serviceOrderId, questionId, value } = action
      const isMultiEditing = state.selected.has(serviceOrderId)

      const rows = state.rows.map(row => {
        if (row.serviceOrder.id === serviceOrderId) return setRowReplyValue(row, questionId, value)
        if (isMultiEditing && state.selected.has(row.serviceOrder.id)) return setRowReplyValue(row, questionId, value)
        return row
      })

      return { ...state, rows }
    }

    case 'OPEN_WIDE_EDITOR':
      return { ...state, wideEditor: { serviceOrderId: action.serviceOrderId, questionId: action.questionId } }

    case 'CLOSE_WIDE_EDITOR':
      return { ...state, wideEditor: null }

    // Usado pela ação "Concluir"/"Pausar" por linha (ver `bulk/_requests/
    // bulkRowActions.js`): depois que UMA atividade é salva individualmente
    // com sucesso, ela sai da grade (o refetch das queries da lista já cobre
    // o resto da UI fora deste modal).
    case 'REMOVE_ROW': {
      const rows = state.rows.filter(row => row.serviceOrder.id !== action.serviceOrderId)
      const selected = new Set(state.selected)
      selected.delete(action.serviceOrderId)
      return { ...state, rows, selected }
    }

    default:
      return state
  }
}

const BulkGridStateProvider = ({ children }) => {
  const serviceOrders = useBulkServiceOrders()
  const questions = useQuestions()

  const [state, dispatch] = React.useReducer(reducer, null, () => ({
    rows: initRows(serviceOrders, questions),
    selected: new Set(),
    wideEditor: null
  }))

  const value = React.useMemo(() => ({ state, dispatch, questions }), [state, questions])

  return <Context.Provider value={value}>{children}</Context.Provider>
}

const EMPTY_STATE = { rows: [], selected: new Set(), wideEditor: null }

const useBulkGridContext = () => React.useContext(Context)

export const useBulkGridColumns = () => {
  const ctx = useBulkGridContext()
  return gridColumns(ctx?.questions || [])
}

export const useBulkGridRows = () => useBulkGridContext()?.state?.rows || EMPTY_STATE.rows

export const useBulkGridSelection = () => {
  const ctx = useBulkGridContext()
  const state = ctx?.state || EMPTY_STATE
  const dispatch = ctx?.dispatch || (() => {})

  return {
    selected: state.selected,
    allSelected: state.rows.length > 0 && state.rows.every(row => state.selected.has(row.serviceOrder.id)),
    toggleRow: serviceOrderId => dispatch({ type: 'TOGGLE_ROW', serviceOrderId }),
    toggleAll: () => dispatch({ type: 'TOGGLE_ALL' })
  }
}

export const useBulkGridCellReply = (serviceOrderId, questionId) => {
  const rows = useBulkGridRows()
  const row = rows.find(item => item.serviceOrder.id === serviceOrderId)
  return findRowReply(row?.replies, questionId)
}

export const useBulkGridActions = () => {
  const ctx = useBulkGridContext()
  const dispatch = ctx?.dispatch || (() => {})

  return {
    setCellValue: (serviceOrderId, questionId, value) => dispatch({ type: 'SET_CELL', serviceOrderId, questionId, value }),
    openWideEditor: (serviceOrderId, questionId) => dispatch({ type: 'OPEN_WIDE_EDITOR', serviceOrderId, questionId }),
    closeWideEditor: () => dispatch({ type: 'CLOSE_WIDE_EDITOR' }),
    removeRow: serviceOrderId => dispatch({ type: 'REMOVE_ROW', serviceOrderId })
  }
}

export const useBulkGridWideEditorTarget = () => useBulkGridContext()?.state?.wideEditor || null

// Mesma lógica de `useBulkGridPayload`, mas para UMA linha só — usada pelas
// ações "Concluir"/"Pausar" por linha. Quando `shouldValidate` encontra uma
// pergunta obrigatória sem resposta NAQUELA linha, retorna `invalidReply` em
// vez de `entry` (mesmo contrato de `firstInvalidRowReply`/`invalidRow` do
// payload de todas as linhas).
export const useBulkGridRowPayload = () => {
  const ctx = useBulkGridContext()
  const rows = ctx?.state?.rows || EMPTY_STATE.rows

  return (serviceOrderId, { shouldValidate } = {}) => {
    const row = rows.find(item => item.serviceOrder.id === serviceOrderId)
    if (!row) return { entry: null, serviceOrder: null }

    if (shouldValidate) {
      const invalidReply = firstInvalidRowReply(row.replies)
      if (invalidReply) return { invalidReply, serviceOrder: row.serviceOrder }
    }

    return {
      entry: { serviceOrderId: row.serviceOrder.id, replies: cleanRepliesForApi(row.replies) },
      serviceOrder: row.serviceOrder
    }
  }
}

// Retorna uma função que calcula `{ entries }` (ou `{ invalidRow }` quando
// `shouldValidate` encontra uma pergunta obrigatória sem resposta em alguma
// linha) prontos para a mutation `bulkExecuteServiceOrders`. Sempre inclui
// uma entrada por atividade selecionada — mesmo as não editadas — para que
// elas continuem fazendo parte da conclusão/edição em massa.
export const useBulkGridPayload = () => {
  const ctx = useBulkGridContext()
  const rows = ctx?.state?.rows || EMPTY_STATE.rows

  return ({ shouldValidate } = {}) => {
    if (shouldValidate) {
      for (const row of rows) {
        const invalid = firstInvalidRowReply(row.replies)
        if (invalid) return { invalidRow: { serviceOrder: row.serviceOrder, reply: invalid } }
      }
    }

    const entries = rows.map(row => ({
      serviceOrderId: row.serviceOrder.id,
      replies: cleanRepliesForApi(row.replies)
    }))

    return { entries }
  }
}

export default BulkGridStateProvider
